<template>
  <section class="request-detail-response" data-testid="request-detail-response">
    <div class="request-detail-response__card">
      <v-progress-linear
        v-if="loading"
        indeterminate
        height="2"
        color="primary"
        class="request-detail-response__loader"
        data-testid="request-detail-response__loader"
      />

      <div class="request-detail-response__header">
        <div class="request-detail-response__title-group">
          <p class="request-detail-response__heading">Response</p>
          <span
            v-if="responseTypeLabel"
            class="request-detail-response__type-chip"
            data-testid="request-detail-response__type-chip"
          >
            {{ responseTypeLabel }}
          </span>
        </div>

        <div class="request-detail-response__header-actions">
          <template v-if="isComposing && isPendingSubmission">
            <v-btn
              small
              outlined
              class="request-detail-response__btn request-detail-response__btn--outlined"
              data-testid="request-detail-response__save"
              :disabled="loading"
              :loading="loading && pendingSubmitAction === 'save'"
              @click="saveCompose"
            >
              Save
            </v-btn>
            <v-btn
              small
              depressed
              color="primary"
              class="request-detail-response__btn request-detail-response__btn--primary"
              :disabled="loading"
              :loading="loading && pendingSubmitAction === 'submit'"
              data-testid="request-detail-response__submit"
              @click="submitCompose"
            >
              <v-icon left small>mdi-check</v-icon>
              Submit response
            </v-btn>
          </template>

          <template v-else-if="isComposing">
            <v-btn
              small
              outlined
              class="request-detail-response__btn request-detail-response__btn--outlined"
              data-testid="request-detail-response__cancel-compose"
              :disabled="loading"
              @click="cancelCompose"
            >
              Cancel
            </v-btn>
            <v-btn
              small
              depressed
              color="primary"
              class="request-detail-response__btn request-detail-response__btn--primary"
              :disabled="loading"
              :loading="loading"
              data-testid="request-detail-response__submit"
              @click="submitCompose"
            >
              <v-icon left small>mdi-check</v-icon>
              Submit response
            </v-btn>
          </template>

          <template v-else-if="showContentActions">
            <button
              v-if="showDownloadAll"
              type="button"
              class="request-detail-response__icon-btn"
              aria-label="Download all"
              data-testid="request-detail-response__download-all"
              @click="$emit('download-all')"
            >
              <img
                src="/img/icons/fbc-icons/DownloadIcon.svg"
                alt=""
                class="request-detail-response__icon-btn-img"
                width="16"
                height="16"
              >
            </button>

            <v-btn
              v-if="canEditResponse"
              small
              depressed
              color="primary"
              class="request-detail-response__btn request-detail-response__btn--primary"
              data-testid="request-detail-response__edit-response"
              @click="startCompose"
            >
              <v-icon left small>mdi-pencil-outline</v-icon>
              {{ editActionLabel }}
            </v-btn>
          </template>
        </div>
      </div>

      <div class="request-detail-response__body">
        <!-- API in progress: keep layout stable -->
        <div
          v-if="loading"
          class="request-detail-response__loading"
          data-testid="request-detail-response__loading"
        >
          <v-skeleton-loader type="list-item-two-line" class="mb-2" />
          <v-skeleton-loader type="list-item-two-line" class="mb-2" />
          <v-skeleton-loader type="image" height="96" />
        </div>

        <!-- Pending Submission: empty state -->
        <div
          v-else-if="isPendingSubmission && !isComposing && !hasDraftContent"
          class="request-detail-response__empty"
          data-testid="request-detail-response__empty"
        >
          <div class="request-detail-response__empty-icon" aria-hidden="true">
            <v-icon size="20">mdi-clock-outline</v-icon>
          </div>
          <div class="request-detail-response__empty-copy">
            <p class="request-detail-response__empty-title">
              Waiting for {{ assigneeName }} to submit
            </p>
            <p class="request-detail-response__empty-text">
              No response yet. Send a reminder, or submit a response received on their behalf.
            </p>
          </div>
          <v-btn
            v-if="canSubmitOnBehalf"
            color="primary"
            depressed
            class="request-detail-response__btn request-detail-response__btn--primary"
            data-testid="request-detail-response__start-submit"
            @click="startCompose"
          >
            <v-icon left small>mdi-check-circle-outline</v-icon>
            Submit response
          </v-btn>
        </div>

        <!-- Compose / edit -->
        <div
          v-else-if="isComposing"
          class="request-detail-response__compose"
          data-testid="request-detail-response__compose"
        >
          <div
            v-if="isApproved && approvedBannerText"
            class="request-detail-response__approved-banner"
            data-testid="request-detail-response__approved-banner"
          >
            <v-icon small class="mr-1">mdi-check-circle-outline</v-icon>
            <p class="request-detail-response__approved-banner-text">{{ approvedBannerText }}</p>
          </div>

          <div
            v-else
            class="request-detail-response__context-banner"
            :class="{
              'request-detail-response__context-banner--muted': isEditingExisting,
            }"
          >
            <v-icon small class="request-detail-response__context-banner-icon">mdi-information-outline</v-icon>
            <p class="request-detail-response__context-banner-text">
              {{ composeBannerText }}
            </p>
          </div>

          <div class="request-detail-response__form-card">
            <!-- Text: written response mandatory -->
            <div
              v-if="isTextType"
              class="request-detail-response__compose-field"
            >
              <p class="request-detail-response__field-label">
                <span class="request-detail-response__field-label-strong">Written Response*</span>
              </p>
              <lb-textBox
                :value="form.response"
                label=""
                requriedrows="5"
                placeholder="Enter the written response"
                :hidedetails="true"
                class="request-detail-response__textarea"
                data-testid="request-detail-response__response-text"
                @input="form.response = $event"
              />
            </div>

            <!-- File upload (required) or text optional attachments -->
            <div
              v-if="supportsAttachments"
              class="request-detail-response__compose-field"
              :class="{ 'request-detail-response__compose-field--upload': isTextType }"
            >
              <p class="request-detail-response__field-label">
                <span class="request-detail-response__field-label-strong">
                  {{ isFileType ? 'Upload File*' : 'Upload File ' }}
                </span>
                <span
                  v-if="isTextType"
                  class="request-detail-response__field-label-hint--muted"
                >(optional)</span>
              </p>

              <div class="request-detail-response__compose-field-body">
                <div
                  v-for="(file, index) in form.retainedAttachments"
                  :key="`existing-file-${attachmentKey(file, index)}`"
                  class="request-detail-response__file-card"
                >
                  <div class="request-detail-response__file-icon" aria-hidden="true">
                    {{ fileExtension(file) }}
                  </div>
                  <div class="request-detail-response__file-meta">
                    <p class="request-detail-response__file-name">{{ fileName(file) }}</p>
                    <p v-if="formatFileSize(file)" class="request-detail-response__file-size">
                      {{ formatFileSize(file) }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="request-detail-response__file-remove"
                    :aria-label="`Remove ${fileName(file)}`"
                    :data-testid="`request-detail-response__remove-file--${index}`"
                    :disabled="loading"
                    @click="removeRetainedAttachment(index)"
                  >
                    <img
                      src="/img/icons/fbc-icons/DeleteActions.svg"
                      alt=""
                      width="16"
                      height="16"
                    >
                  </button>
                </div>

                <FcDragDropFile
                  :files="form.files"
                  class="request-detail-response__dropzone"
                  :data-testid="isFileType
                    ? 'request-detail-response__file-drop'
                    : 'request-detail-response__file-drop-optional'"
                  @update:files="form.files = $event"
                />
              </div>
            </div>

            <!-- Link: configured URL only (no file download) -->
            <div
              v-else-if="isLinkType"
              class="request-detail-response__compose-field"
            >
              <p class="request-detail-response__field-label">
                <span class="request-detail-response__field-label-strong">Upload Link</span>
              </p>
              <a
                v-if="request.uploadlink"
                :href="request.uploadlink"
                target="_blank"
                rel="noopener noreferrer"
                class="request-detail-response__link-input"
                data-testid="request-detail-response__link"
              >
                <v-icon small>mdi-link-variant</v-icon>
                <span>{{ request.uploadlink }}</span>
              </a>
              <span v-else class="request-detail-response__muted">No link configured</span>
            </div>

            <!-- Remarks optional for all response types -->
            <div class="request-detail-response__compose-field request-detail-response__compose-field--remark">
              <p class="request-detail-response__field-label">
                <span class="request-detail-response__field-label-strong">Remarks</span>
                <span class="request-detail-response__field-label-hint"> (optional, explain what changed)</span>
              </p>
              <lb-textBox
                :value="form.remark"
                label=""
                requriedrows="1"
                placeholder="e.g. Received by email from owner; corrected page 3"
                :hidedetails="true"
                data-testid="request-detail-response__remark"
                @input="form.remark = $event"
              />
            </div>
          </div>
        </div>

        <!-- Draft (pending submission) / Pending Approval / Approved: review view -->
        <div
          v-else-if="showReviewContent || hasDraftContent"
          class="request-detail-response__review"
          data-testid="request-detail-response__review"
        >
          <div
            v-if="isApproved && approvedBannerText"
            class="request-detail-response__approved-banner"
            data-testid="request-detail-response__approved-banner"
          >
            <v-icon small class="mr-1">mdi-check-circle-outline</v-icon>
            <p class="request-detail-response__approved-banner-text">{{ approvedBannerText }}</p>
          </div>

          <div
            v-else-if="isPendingApproval && submittedBannerText"
            class="request-detail-response__context-banner request-detail-response__context-banner--muted"
            data-testid="request-detail-response__submitted-banner"
          >
            <v-icon small class="request-detail-response__context-banner-icon">mdi-information-outline</v-icon>
            <p class="request-detail-response__context-banner-text">{{ submittedBannerText }}</p>
          </div>

          <div
            v-else-if="isPendingSubmission && hasDraftContent"
            class="request-detail-response__context-banner"
            data-testid="request-detail-response__draft-banner"
          >
            <v-icon small class="request-detail-response__context-banner-icon">mdi-information-outline</v-icon>
            <p class="request-detail-response__context-banner-text">
              Draft saved — not yet submitted
            </p>
          </div>

          <div
            v-if="isTextType && responseText"
            class="request-detail-response__text-block"
            data-testid="request-detail-response__text-content"
          >
            <p class="request-detail-response__field-label">
              <span class="request-detail-response__field-label-strong">Response</span>
            </p>
            <div class="request-detail-response__text-card">
              <p class="request-detail-response__text-value">{{ responseText }}</p>
            </div>
          </div>

          <div
            v-if="supportsAttachments && attachments.length"
            class="request-detail-response__text-block"
            data-testid="request-detail-response__files"
          >
            <p class="request-detail-response__field-label">
              <span class="request-detail-response__field-label-strong">Files</span>
            </p>
            <RequestDetailResponseFiles
              :files="attachments"
              @download="$emit('download-file', $event)"
            />
          </div>
          <a
            v-if="isLinkType && request.uploadlink"
            :href="request.uploadlink"
            target="_blank"
            rel="noopener noreferrer"
            class="request-detail-response__link-card"
            data-testid="request-detail-response__review-link"
          >
            <div class="request-detail-response__link-card-icon" aria-hidden="true">
              <v-icon size="16">mdi-link-variant</v-icon>
            </div>
            <div class="request-detail-response__link-card-body">
              <p class="request-detail-response__link-card-url">{{ request.uploadlink }}</p>
              <p v-if="linkMetaText" class="request-detail-response__link-card-meta">
                {{ linkMetaText }}
              </p>
            </div>
            <v-icon small>mdi-arrow-top-right</v-icon>
          </a>

          <div
            v-if="remarkText"
            class="request-detail-response__text-block"
            data-testid="request-detail-response__remark-display"
          >
            <p class="request-detail-response__field-label">
              <span class="request-detail-response__field-label-strong">Remarks</span>
            </p>
            <p class="request-detail-response__text-value">{{ remarkText }}</p>
          </div>

          <p
            v-if="!hasReviewContent"
            class="request-detail-response__muted"
            data-testid="request-detail-response__no-content"
          >
            No response content available.
          </p>
        </div>

        <!-- Cancelled -->
        <div
          v-else-if="isCancelled"
          class="request-detail-response__empty"
          data-testid="request-detail-response__cancelled"
        >
          <p class="request-detail-response__empty-text">This request has been cancelled.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import './RequestDetailResponse.scss';
import { FcDragDropFile } from '@/modules/financialclose/components';
import RequestDetailResponseFiles from './RequestDetailResponseFiles.vue';
import {
  getRequestAttachments,
  canDownloadAttachments,
  normalizeRequestType,
  resolveResponseTypeLabel,
  buildResponseSubmitPayload,
  validateResponseSubmit,
  resolveAttachmentKey,
} from '../../utils/requestDetail';

export default {
  name: 'RequestDetailResponse',
  components: {
    FcDragDropFile,
    RequestDetailResponseFiles,
  },
  props: {
    request: {
      type: Object,
      required: true,
    },
    canSubmitOnBehalf: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isComposing: false,
      isEditingExisting: false,
      pendingSubmitAction: null,
      form: {
        response: '',
        remark: '',
        files: [],
        retainedAttachments: [],
      },
    };
  },
  computed: {
    requestType() {
      return normalizeRequestType(this.request?.type);
    },
    isFileType() {
      return this.requestType === 'fileupload';
    },
    isTextType() {
      return this.requestType === 'text';
    },
    isLinkType() {
      return this.requestType === 'link';
    },
    /** Text and fileupload may carry attachments; link never does. */
    supportsAttachments() {
      return this.isFileType || this.isTextType;
    },
    responseTypeLabel() {
      return resolveResponseTypeLabel(this.request?.type);
    },
    assigneeName() {
      return this.request?.info_owner_detail?.name
        || this.request?.info_owner_detail?.email
        || 'the information owner';
    },
    submitterName() {
      if (this.isComposing) {
        const sessionUser = this.$store.state.user || {};
        return sessionUser.name || sessionUser.email || 'User';
      }

      const updatedByDetail = this.request?.updated_by_detail;
      if (updatedByDetail?.name) return updatedByDetail.name;
      if (updatedByDetail?.email) return updatedByDetail.email;

      const updatedById = this.request?.updated_by;
      if (updatedById) {
        const users = [
          ...(this.$store.getters['informationManager/userList'] || []),
          ...(this.$store.getters['tasks/getFormDataV3']?.users || []),
        ];
        const matchedUser = users.find((user) => String(user._id) === String(updatedById));
        if (matchedUser?.name) return matchedUser.name;
        if (matchedUser?.displayname) return matchedUser.displayname;
        if (matchedUser?.email || matchedUser?.contactemail) {
          return matchedUser.email || matchedUser.contactemail;
        }
      }

      return 'Unknown user';
    },
    isPendingSubmission() {
      return this.request?.stage === 0;
    },
    isPendingApproval() {
      return this.request?.stage === 1;
    },
    isApproved() {
      return this.request?.stage === 2;
    },
    isCancelled() {
      return this.request?.stage === -1;
    },
    showReviewContent() {
      return this.isPendingApproval || this.isApproved;
    },
    attachments() {
      return this.supportsAttachments ? getRequestAttachments(this.request) : [];
    },
    responseText() {
      if (!this.isTextType) return '';
      const value = this.request?.response;
      return typeof value === 'string' ? value.trim() : '';
    },
    remarkText() {
      return (this.request?.remark || '').trim();
    },
    hasDraftContent() {
      if (!this.isPendingSubmission) return false;
      return this.attachments.length > 0 || !!this.responseText || !!this.remarkText;
    },
    hasReviewContent() {
      return this.attachments.length > 0
        || !!this.responseText
        || !!this.remarkText
        || (this.isLinkType && !!this.request?.uploadlink);
    },
    showContentActions() {
      return this.showReviewContent || this.hasDraftContent;
    },
    canEditResponse() {
      if (this.isApproved) return false;
      if (this.hasDraftContent) return this.canSubmitOnBehalf;
      return this.canSubmitOnBehalf || (this.canEdit && this.isPendingApproval);
    },
    editActionLabel() {
      return this.isPendingSubmission ? 'Edit' : 'Edit response';
    },
    showDownloadAll() {
      return canDownloadAttachments(this.request);
    },
    composeBannerText() {
      if (this.isEditingExisting) {
        return `Editing response as ${this.submitterName}`;
      }
      return `Submitting as ${this.submitterName}`;
    },
    submittedBannerText() {
      return `Submitted by ${this.submitterName}`;
    },
    approvedBannerText() {
      const approver = this.request?.approved_by_detail?.name
        || this.request?.updated_by_detail?.name
        || this.request?.approved_by_name
        || '';
      const date = this.formatShortDate(this.request?.approved_at || this.request?.updated_at);
      if (approver && date) return `Approved by ${approver} · ${date}`;
      if (approver) return `Approved by ${approver}`;
      if (date) return `Approved · ${date}`;
      return 'Approved';
    },
    linkMetaText() {
      const parts = [];
      const submitter = this.submitterName;
      if (submitter) parts.push(submitter);
      if (this.request?.version != null) parts.push(`v${this.request.version}`);
      const date = this.formatShortDate(this.request?.updated_at || this.request?.submitted_at);
      const time = this.formatShortTime(this.request?.updated_at || this.request?.submitted_at);
      if (date) parts.push(date);
      if (time) parts.push(time);
      return parts.join(' · ');
    },
  },
  watch: {
    'request._id'() {
      this.resetCompose();
    },
    'request.stage'() {
      if (!this.loading) this.resetCompose();
    },
  },
  methods: {
    attachmentKey: resolveAttachmentKey,
    startCompose() {
      this.isComposing = true;
      this.isEditingExisting = this.showReviewContent || this.hasDraftContent;
      this.pendingSubmitAction = null;
      this.form = {
        response: this.responseText || '',
        remark: this.remarkText || '',
        files: [],
        retainedAttachments: this.supportsAttachments ? [...this.attachments] : [],
      };
    },
    cancelCompose() {
      this.resetCompose();
    },
    resetCompose() {
      this.isComposing = false;
      this.isEditingExisting = false;
      this.pendingSubmitAction = null;
      this.form = {
        response: '',
        remark: '',
        files: [],
        retainedAttachments: [],
      };
    },
    removeRetainedAttachment(index) {
      const next = [...this.form.retainedAttachments];
      next.splice(index, 1);
      this.form.retainedAttachments = next;
    },
    fileName(file) {
      return file?.uploadname || file?.originalname || file?.file_name || file?.name || 'Document';
    },
    fileExtension(file) {
      const name = this.fileName(file);
      if (!name || !name.includes('.')) return 'FILE';
      const ext = name.split('.').pop().toLowerCase();
      return (ext || 'FILE').toUpperCase().slice(0, 4);
    },
    formatFileSize(file) {
      const size = file?.size || file?.filesize;
      if (!size && size !== 0) return '';
      const numeric = Number(size);
      if (!Number.isFinite(numeric) || numeric <= 0) return '';
      if (numeric < 1024) return `${numeric} B`;
      if (numeric < 1024 * 1024) return `${(numeric / 1024).toFixed(1)} KB`;
      return `${(numeric / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatShortDate(value) {
      if (!value) return '';
      return this.$nova?.formatDateTime
        ? this.$nova.formatDateTime(value, 'DD MMM YYYY')
        : new Date(value).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatShortTime(value) {
      if (!value) return '';
      return this.$nova?.formatDateTime
        ? this.$nova.formatDateTime(value, 'HH:mm')
        : new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    saveCompose() {
      this.dispatchCompose(false, 'save');
    },
    submitCompose() {
      this.dispatchCompose(true, 'submit');
    },
    dispatchCompose(submit, action) {
      const options = { submit };
      const error = validateResponseSubmit(this.request, this.form, options);
      if (error) {
        this.$store.commit('sbError', error, { root: true });
        return;
      }
      const payload = buildResponseSubmitPayload(this.request, this.form, options);
      this.pendingSubmitAction = action;
      this.$emit('submit-response', payload, (succeeded) => {
        this.pendingSubmitAction = null;
        if (succeeded) this.resetCompose();
      });
    },
  },
};
</script>
